import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { z } from 'zod'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { AuthHero } from '@/features/auth/AuthHero'
import { useAuth } from '@/features/auth/auth-context'
import { holdPreAuth } from '@/features/auth/pre-auth'
import { ApiError } from '@/lib/api/problem'
import { describeError } from '@/components/ui/error-copy'

const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/u

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { signIn } = useAuth()
  const [formError, setFormError] = useState<string | null>(null)

  const schema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .trim()
          .min(1, { message: t('auth.errors.emailRequired') })
          .regex(emailPattern, { message: t('auth.errors.emailInvalid') }),
        password: z.string().min(1, { message: t('auth.errors.passwordRequired') }),
      }),
    [t],
  )

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null)

    try {
      const response = await signIn(values.email, values.password)

      if (response.twoFactorRequired === true && response.preAuthToken !== undefined) {
        holdPreAuth({
          token: response.preAuthToken,
          expiresAt: response.preAuthTokenExpiresAt ?? null,
        })
        await navigate('/login/2fa', { replace: true })
        return
      }

      await navigate('/', { replace: true })
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setFormError(t('auth.signIn.invalid'))
        return
      }

      setFormError(t(describeError(error).description))
    }
  })

  return (
    <AuthHero
      title={t('auth.signIn.title')}
      subtitle={t('auth.signIn.subtitle')}
      headerLink={{ to: '/register', label: t('auth.hero.apply') }}
      footer={
        <>
          {`${t('auth.signIn.noAccount')} `}
          <Link to="/register" className="font-medium text-accent-text underline">
            {t('auth.signIn.register')}
          </Link>
        </>
      }
    >
      <form noValidate onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4">
        <Input
          label={t('auth.signIn.email')}
          type="email"
          autoComplete="email"
          autoFocus
          required
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />

        <Input
          label={t('auth.signIn.password')}
          type="password"
          autoComplete="current-password"
          required
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />

        {formError === null ? null : (
          <p role="alert" className="text-sm text-error-strong">
            {formError}
          </p>
        )}

        <Button type="submit" loading={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? t('auth.signIn.submitting') : t('auth.signIn.submit')}
        </Button>
      </form>
    </AuthHero>
  )
}
